/**
 * Interpretazione della risposta del server alla verifica della sessione associato.
 *
 * Perché esiste: il 29/08/2026, ruotando JWT_SECRET sul VPS (hardening), tutti i token
 * associato emessi prima sono diventati «invalid signature». La guardia dell'area
 * riservata controllava solo che il token esistesse e non fosse scaduto (lettura locale
 * del payload): il sito SEMBRAVA loggato, ogni chiamata API prendeva 401 e l'estensione
 * Takata — che riceve lo stesso token dal sito — diceva «Accedi all'area riservata» a chi
 * c'era già dentro. Ci si usciva solo con logout + login. Migliaia di 401 in una settimana.
 *
 * Funzione pura, senza axios (i test CRA non digeriscono i moduli ESM di axios).
 *
 *  - 'valida'        → il server riconosce il token
 *  - 'rifiutata'     → il server lo respinge in modo definitivo: firma non valida o
 *                      scaduto (401), non è un token associato (403), associato
 *                      inesistente (404). Va fatto logout: riprovare non serve.
 *  - 'indeterminata' → non si sa (rete assente, 5xx, 429...): NON si fa logout, perché
 *                      buttare una sessione buona per un server in affanno è peggio.
 */
export type EsitoSessione = 'valida' | 'rifiutata' | 'indeterminata';

export function esitoSessioneDaStatus(status: number | undefined | null, codice?: string): EsitoSessione {
  if (typeof status !== 'number') return 'indeterminata';
  if (status >= 200 && status < 300) return 'valida';
  // Dipendente dello studio (Ruling R16): con lo studio non in regola la sessione resta viva,
  // si chiudono solo le sezioni. Il titolare non riceve mai questo codice.
  if (motivoFineDipendente(status, codice) === 'studio_non_in_regola') return 'indeterminata';
  if (status === 401 || status === 403 || status === 404) return 'rifiutata';
  return 'indeterminata';
}

/**
 * Dipendente dello studio (Ruling R15/R16), codici del backend `{ success:false, code, message }`
 * (o `extensions.code` dal contesto GraphQL):
 *  - 'sessione'             → 401 `DIPENDENTI_SPENTI` o `SESSIONE_NON_VALIDA`: la sessione è finita
 *                             (funzione spenta, dipendente sospeso/eliminato, password cambiata);
 *  - 'studio_non_in_regola' → 403 `STUDIO_NON_IN_REGOLA`: la sessione resta, le sezioni si chiudono.
 */
export type MotivoFineDipendente = 'sessione' | 'studio_non_in_regola';

export function motivoFineDipendente(status: number | undefined | null, codice: string | undefined | null): MotivoFineDipendente | null {
  if (status === 401 && (codice === 'DIPENDENTI_SPENTI' || codice === 'SESSIONE_NON_VALIDA')) return 'sessione';
  if (status === 403 && codice === 'STUDIO_NON_IN_REGOLA') return 'studio_non_in_regola';
  return null;
}

/** Codice d'errore del corpo di una risposta: `code` (REST) o `errors[].extensions.code` (GraphQL). */
export function codiceDaRisposta(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const corpo = data as { code?: unknown; errors?: Array<{ extensions?: { code?: unknown } } | null> };
  if (typeof corpo.code === 'string' && corpo.code) return corpo.code;
  if (Array.isArray(corpo.errors)) {
    for (const e of corpo.errors) {
      const code = e?.extensions?.code;
      if (typeof code === 'string' && code) return code;
    }
  }
  return undefined;
}

export const MESSAGGIO_SESSIONE_FINITA = 'La sessione non è più valida: accedi di nuovo';
export const MESSAGGIO_STUDIO_NON_IN_REGOLA =
  'Lo studio non risulta in regola: le sezioni sono momentaneamente chiuse. Rivolgiti al titolare.';
